/**
 * generateCandidates — "give us anything → 2-3 GENERATED full themes."
 *
 * Resolves a SEED (+ polarity suggestion) from the source — a URL (via the real
 * backend ingest), a logo (pre-resolved seed), or nothing — then fans it across
 * treatments through the mirrored engine. Emits ONE event: "Candidates Generated".
 */
import {
  generateCandidatesFromSeed,
  seedFromPalette,
  normalizeSeed,
  type Polarity,
} from "@/data/theme-engine";
import { track } from "@/lib/analytics";
import type {
  GenerateCandidatesInput,
  GenerateCandidatesResult,
  ThemeCommandClient,
} from "./types";

/** pop.in signature violet — the fallback seed when there's no usable input. */
const DEFAULT_SEED = "#7c3aed";

export async function generateCandidates(
  input: GenerateCandidatesInput,
  deps: { client: Pick<ThemeCommandClient, "generateThemeColorsFromUrl"> },
): Promise<GenerateCandidatesResult> {
  let seed = DEFAULT_SEED;
  let polarity: Polarity = "dark";
  let ingestMessage: string | null = null;
  let usedFallbackSeed = false;

  if (input.source.kind === "url") {
    const res = await deps.client.generateThemeColorsFromUrl({
      brandId: input.brandId,
      url: input.source.url,
    });
    if (res.status === "SUCCESS" && res.palette) {
      const derived = seedFromPalette(res.palette);
      seed = derived.seed;
      polarity = derived.polarity;
    } else {
      usedFallbackSeed = true;
      ingestMessage = res.message ?? null;
    }
  } else if (input.source.seed) {
    try {
      seed = normalizeSeed(input.source.seed);
    } catch {
      usedFallbackSeed = true;
    }
  } else {
    usedFallbackSeed = true;
  }

  const candidates = generateCandidatesFromSeed(seed, {
    polarity,
    fontId: input.fontId,
    limit: input.limit ?? 3,
  });

  track("Candidates Generated", {
    source: input.source.kind,
    seed,
    polarity,
    count: candidates.length,
    treatmentIds: candidates.map((c) => c.recipe.treatmentId),
    usedFallbackSeed,
  });

  return { candidates, seed, polarity, source: input.source.kind, ingestMessage, usedFallbackSeed };
}
